<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
        Quality Profile & Benchmarks
      </h3>
      <button @click="$emit('back')" class="btn btn-secondary btn-sm">
        Back to Results
      </button>
    </div>

    <!-- Competitor Quality Cards -->
    <div class="space-y-3 mb-6">
      <div v-for="comp in competitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span :class="gradeBadge(comp.overall_quality_grade)" class="px-2.5 py-1 rounded text-sm font-bold">
              {{ comp.overall_quality_grade }}
            </span>
            <div>
              <div class="text-sm font-medium">
                #{{ comp.serp_position }} {{ comp.domain }}
              </div>
              <div class="flex gap-2 mt-1">
                <span v-for="(grade, dim) in comp.quality_grades || {}" :key="dim" :class="gradeBadge(grade)" class="px-1.5 py-0.5 rounded text-xs">
                  {{ dim }}: {{ grade }}
                </span>
              </div>
            </div>
          </div>
          <select
            v-model="tierAdjustments[comp.id]"
            class="rounded-lg border-gray-300 dark:border-gray-600 text-sm">
            <option value="elevated">
              Elevated
            </option>
            <option value="standard">
              Standard
            </option>
            <option value="deprioritized">
              Deprioritized
            </option>
            <option value="excluded">
              Excluded
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Benchmarks Table -->
    <div v-if="benchmarks.length" class="mb-6">
      <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
        Raw vs Cleaned Benchmarks
      </h4>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200 dark:border-gray-700">
              <th class="text-left py-2 pr-4 text-xs text-gray-500 uppercase">
                Metric
              </th>
              <th class="text-right py-2 px-4 text-xs text-gray-500 uppercase">
                Raw
              </th>
              <th class="text-right py-2 px-4 text-xs text-gray-500 uppercase">
                Cleaned
              </th>
              <th class="text-right py-2 pl-4 text-xs text-gray-500 uppercase">
                Inflation
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bm in benchmarks" :key="bm.metric_name" class="border-b border-gray-100 dark:border-gray-800">
              <td class="py-2 pr-4 text-gray-700 dark:text-gray-300 capitalize">
                {{ bm.metric_name.replace(/_/g, ' ') }}
              </td>
              <td class="py-2 px-4 text-right text-gray-500">
                {{ formatNum(bm.raw_value) }}
              </td>
              <td class="py-2 px-4 text-right font-medium text-gray-900 dark:text-gray-100">
                {{ formatNum(bm.cleaned_value) }}
              </td>
              <td class="py-2 pl-4 text-right" :class="bm.inflation_percentage > 0 ? 'text-amber-600' : 'text-green-600'">
                {{ bm.inflation_percentage > 0 ? '+' : '' }}{{ bm.inflation_percentage }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Approve Button -->
    <div class="flex justify-end">
      <button
        @click="handleApprove"
        :disabled="approving"
        class="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
        {{ approving ? 'Saving...' : 'Approve Quality Profile' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { approveQualityProfile } from '~/javascript/api/competitor_analysis.js'

const props = defineProps({
  competitors: { type: Array, required: true },
  benchmarks: { type: Array, required: true },
  serpAnalysis: { type: Object, required: true },
  pageId: { type: String, required: true }
})

const emit = defineEmits(['approved', 'back'])

const approving = ref(false)

const tierAdjustments = reactive({})
props.competitors.forEach((c) => {
  tierAdjustments[c.id] = c.benchmark_tier || 'standard'
})

const gradeBadge = (grade) => {
  const map = {
    A: 'bg-green-100 dark:bg-green-900/30 text-green-700',
    B: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700',
    C: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700',
    D: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700',
    F: 'bg-red-100 dark:bg-red-900/30 text-red-700'
  }
  return map[grade] || map.F
}

const formatNum = (val) => {
  if (val == null) return '-'
  return Number(val).toFixed(1)
}

const handleApprove = async() => {
  approving.value = true
  try {
    await approveQualityProfile(props.pageId, tierAdjustments)
    emit('approved')
  } catch (e) {
    console.error('Error approving:', e)
  } finally {
    approving.value = false
  }
}
</script>
